import { theme } from '../theme';
import { CATEGORY_LABELS, getCategoriesForReasonCode, expandCategoryRange } from '../constants';
import { ownsMsbKVocabulary, type KnownRole } from './roleRegistry';
import type { ClassificationSchema, ClassificationVocabulary, SchemaValue, VocabularyAttribute } from './api';

const mp = (s: ClassificationSchema | null, id: string) => s?.marktprozess.find(v => v.id === id);
const an = (s: ClassificationSchema | null, id: string) => s?.anliegen.find(v => v.id === id);

// THE placeholder floor. Tier 1-2 (schema/vocabulary truth, tested from the call sites above this
// function) are unchanged and unconditional. Tier 3 — the hardcoded CATEGORY_LABELS word map —
// is gMSB/wMSB's OWN rendered truth, never a generic backstop: a role that carries neither a
// schema nor a vocabulary is not "gMSB-shaped by default", it is a role this seam has not been
// told how to label, and showing it gMSB's German words would be exactly the silent-gmsb-assumption
// this arc's Task 3 already killed at the identity layer. Every other role — including an
// unresolved (null) or a drifted/future (not yet in roleRegistry) role — gets the raw, honest
// placeholder instead of a borrowed word.
export function resolveLabel(schema: ClassificationSchema | null, value: string, role: KnownRole | string | null | undefined): string {
  const fromSchema = schema ? (mp(schema, value)?.label ?? an(schema, value)?.label) : undefined;
  if (fromSchema) return fromSchema;
  if (ownsMsbKVocabulary(role)) return CATEGORY_LABELS[value] ?? value;
  return `{category.${value}}`;
}

// The mapping counterpart of resolveLabel — same seam, same tier order (D10 addendum 4): a served
// grund_code row's OWN minCategory/maxCategory first, the baked REASON_CODES pair as the floor.
// Takes `vocab` directly (unlike resolveLabel, which is handed a pre-resolved label map by its
// caller) because the served attributes this reads (minCategory/maxCategory) live only on the
// secondary axis's raw SchemaValue rows, not on any derived lookup context.tsx already builds.
//
// Tier semantics are per-ID, identical to resolveLabel's `vocabLabels?.get(v) ?? resolveLabel(...)`
// — never per-attribute. D10 addendum 5 (BINDING): labels-served-mapping-baked is banned in ANY
// form. Once a served row exists for `code`, BOTH its label and its mapping come from that row —
// a served row with no min/max attributes is a REAL state (per-role constraint kinds are
// optional-by-shape; the baked floor itself carries this exact state for `sonstiges`/`unbestimmt`),
// and the honest answer is "no derivable range" ([]), never a silent reach into the baked floor's
// range for the same id. Only when the vocabulary serves NO row for `code` at all does the floor
// apply — exactly the state every MSB tenant is in before its vocabulary rows are delivered.
export function resolveCategoriesForSecondaryAxisCode(vocab: ClassificationVocabulary | null | undefined, code: string): string[] {
  const served = vocab?.secondary?.values.find(v => v.id === code);
  if (served) {
    if (!served.minCategory || !served.maxCategory) return [];
    return expandCategoryRange(served.minCategory, served.maxCategory);
  }
  return getCategoriesForReasonCode(code);
}

/** The value's own SchemaValue row, from either axis — the one lookup the three served-presentation
 *  resolvers below share. Primary first: an id can only be on one axis, and the primary axis is the
 *  one every surface asks about most. */
function servedValue(vocab: ClassificationVocabulary | null | undefined, value: string): SchemaValue | undefined {
  if (!vocab) return undefined;
  return vocab.primary.values.find(v => v.id === value) ?? vocab.secondary?.values.find(v => v.id === value);
}

/** The value's IDENTITY HUE: the tenant's served `color`, else the neutral brand tone. Two tiers
 *  (badge-faces round), down from five: every role serves its colours now, and the maps that
 *  stood behind them (theme.categories, theme.marktprozess, theme.wimHandling) are deleted rather
 *  than kept as fallbacks — a fallback map is a second home that drifts. A serve-only bundle on
 *  a backend that serves nothing renders navyMid everywhere, which is honest and is what the
 *  deploy rail's served-faces guard exists to prevent. `schema` stays in the signature for the
 *  call sites; it no longer decides anything here. */
export function resolveColor(_schema: ClassificationSchema | null, vocab: ClassificationVocabulary | null | undefined, value: string): string {
  return servedValue(vocab, value)?.color ?? theme.colors.navyMid;
}

/** Is this the tenant's UNCLASSIFIED primary value, i.e. the one the vocabulary marks
 *  `excludedFromEvaluation`? Keyed to the served marker only: never a role id, never a value-id
 *  literal (part 7 fix loop, 2026-10-12). */
export function isUnclassifiedValue(vocab: ClassificationVocabulary | null | undefined, value: string): boolean {
  return !!vocab?.primary.values.find(v => v.id === value)?.excludedFromEvaluation;
}

/** THE primary-axis paint, decided in one place (part 7 fix loop): the served hue as a filled swatch
 *  with white ink, EXCEPT the unclassified value, which is the quiet slate tint (`theme.unclassifiedSwatch`,
 *  the Erledigt language; operator ruling 2026-10-13, option B). Border width is the same 1.5px in both
 *  forms (each form's border is its OWN fill colour, so the tint shows no rim), so the two registers have
 *  identical metrics and a column mixing them never jumps. `activeFill`/`activeInk` are the filter
 *  bubble's selected state.
 *  <constraints>The unclassified value must read as a FAILED classification, never as another category.
 *  Its served hue (KU's sits next to a real category's) is deliberately ignored here; every primary-axis
 *  register -- band, row badge, strip face, filter bubble, detail/Dashboard badge, settings preview --
 *  reads this ONE rule via `cm.swatch`, so no register can drift back to colouring it. Keyed to the
 *  served `excludedFromEvaluation` marker only. The first served instance of the fleet-wide unclassified
 *  state.</constraints> */
/** `openBorder` (small facts on a row, operator pick C, 2026-10-19): the band's border on the OPEN
 *  (selected) list row — the swatch's own ink at the same width, so the strip's box reappears as a
 *  thin frame when the row's grey selection fill would otherwise swallow it (the unclassified slate
 *  tint sits 1.02:1 on that fill). Slate on the unclassified value, white on a hue. */
export interface PrimarySwatch { fill: string; ink: string; border: string; openBorder: string; activeFill: string; activeInk: string; }
export const PRIMARY_SWATCH_BORDER_WIDTH = '1.5px';
export function resolvePrimarySwatch(schema: ClassificationSchema | null, vocab: ClassificationVocabulary | null | undefined, value: string): PrimarySwatch {
  if (isUnclassifiedValue(vocab, value)) {
    const u = theme.unclassifiedSwatch;
    return { fill: u.fill, ink: u.ink, border: `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${u.fill}`, openBorder: `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${u.ink}`, activeFill: u.activeFill, activeInk: u.activeInk };
  }
  const hue = resolveColor(schema, vocab, value);
  return { fill: hue, ink: theme.colors.white, border: `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${hue}`, openBorder: `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${theme.colors.white}`, activeFill: theme.colors.navy, activeInk: theme.colors.white };
}

/** The value's GLYPH NAME, served or nothing (wim vocabulary v7, 2026-09-13). Deliberately
 *  single-tier where resolveColor has five: a hue has a defensible neutral fallback, a glyph does
 *  not — an icon guessed from an id would assert a meaning the tenant never declared, and the
 *  renderer's honest answer to "no icon" is to render no icon. Returns the NAME from the agreed
 *  set (forward | reply | lookup | archive | question), never markup: the mapping from name to SVG
 *  belongs to the component that owns size, stroke and a11y, and an unknown name must render
 *  nothing rather than a placeholder box. */
export function resolveIcon(vocab: ClassificationVocabulary | null | undefined, value: string): string | null {
  return servedValue(vocab, value)?.icon ?? null;
}

/** The value's HOVER PROSE — the German sentence saying what it MEANS — served or nothing. Same
 *  single-tier reasoning as resolveIcon: there is no baked sentence to fall back to, and inventing
 *  one would put words the tenant never wrote in a tooltip that reads as authoritative. A tooltip
 *  pairs this with the label and shows the label alone when this is null. */
export function resolveHint(vocab: ClassificationVocabulary | null | undefined, value: string): string | null {
  return servedValue(vocab, value)?.hint ?? null;
}

/** The tenant's declaration for ONE classified-alongside attribute, or null when it declares none.
 *  Lives here rather than at a call site so "which attributes does this tenant have?" has one
 *  answer shape, and so a surface can ask about an attribute the payload may not carry without
 *  spelling an optional-chain into the vocabulary's internals. */
export function resolveAttribute(vocab: ClassificationVocabulary | null | undefined, name: string): VocabularyAttribute | null {
  return vocab?.attributes?.find(a => a.name === name) ?? null;
}

/** ONE attribute value's DISPLAY FACE, and the one place the served `faces` map's three states are
 *  read apart (wim vocabulary v7, 2026-09-13):
 *
 *  - a served STRING  → that word, verbatim. The tenant's decision.
 *  - a served `null`   → null, meaning RENDER NO PILL. The unremarkable default (the majority
 *    commodity, a private sender) that a surface would only add noise by repeating — an explicit
 *    decision, which is why it must not collapse into the absent case.
 *  - an ABSENT key     → the PROPERCASED value, because no decision was served and the raw
 *    lowercase id is not a word anybody chose to show. Distinguishing this from `null` is the whole
 *    reason this helper exists: `faces[value] ?? propercase(value)` at a call site would silently
 *    turn every deliberate suppression into a rendered pill.
 *  - no vocabulary / no such attribute → the propercased value, same as an absent key: the
 *    honest reading is "nothing was declared about this", not "this must be hidden". */
export function resolveAttributeFace(vocab: ClassificationVocabulary | null | undefined, name: string, value: string): string | null {
  const faces = resolveAttribute(vocab, name)?.faces;
  if (faces && Object.prototype.hasOwnProperty.call(faces, value)) return faces[value];
  return attributeValueStandIn(value);
}

/** The PROPERCASED stand-in for an attribute value nobody served a face for — the "no decision was
 *  made about this" reading, extracted from the resolver above so the ONE other surface that needs
 *  it cannot spell a second propercase of its own. That surface is the FILTER (FilterPanel): a
 *  bubble must be clickable even for a value whose face is a served `null`, because suppressing a
 *  PILL ("this is the unremarkable default, do not repeat it on every row") says nothing about
 *  whether a clerk may filter for it — so the filter reads attributeFace first and falls here when
 *  it answers null. Nowhere else: a DISPLAY surface that reached for this would be overriding the
 *  tenant's suppression, which is the whole decision resolveAttributeFace exists to carry. */
export function attributeValueStandIn(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** The served WORD for a backend-emitted id that is not a classification value (badge-faces
 *  round): `faces[family][key]`. THREE states, honestly typed (final review — the return type used
 *  to lie and say `string`, so a served explicit `null` passed straight through to a caller
 *  expecting text and crashed the first thing that tried to render it):
 *  - an absent family or an absent key both return the RAW KEY (a string), never a German word
 *    this bundle made up — the same placeholder-floor honesty resolveLabel uses for a role it has
 *    not been told how to label. A raw `offen` on a tab is a deploy-order defect made visible,
 *    which is the point.
 *  - a served STRING is returned verbatim — the tenant's own word.
 *  - a served explicit `null` is returned AS null — "do not put this on a row" (§13.4), the same
 *    suppression resolveAttributeFace's `faces` map already honours. A caller that paints this on
 *    screen must read the three states itself (see shared.tsx's ServedFace, the one place that
 *    does), never coerce it back into a string. */
export function resolveFace(vocab: ClassificationVocabulary | null | undefined, family: string, key: string): string | null {
  const fam = vocab?.faces?.[family];
  if (fam && Object.prototype.hasOwnProperty.call(fam, key)) return fam[key];
  return key;
}

/** The tenant's own WORD for a backend id, or null — the FILTER's reading of the faces map (part 7,
 *  2026-10-11). Unlike resolveFace it has NO raw-key floor: an absent family, an absent key and a
 *  served explicit `null` all answer null. A display surface wants the floor (a raw key on a pill is
 *  a deploy-order defect made visible); a filter section must not exist at all for a fact the tenant
 *  does not serve — a „Warnhinweis" bubble for a flag nobody worded — so it asks this instead and renders nothing on null. */
export function resolveServedWord(vocab: ClassificationVocabulary | null | undefined, family: string, key: string): string | null {
  const fam = vocab?.faces?.[family];
  return fam && Object.prototype.hasOwnProperty.call(fam, key) ? fam[key] : null;
}

/** The shape of `useCategoryMeta().face` — this resolver's own bound form, typed here (its home)
 *  rather than inferred at each call site, so a consumer that needs only the face resolver (not the
 *  whole `useCategoryMeta()` surface) can declare that narrower dependency honestly. First consumer:
 *  `rowPillKinds`' receipt label (part 4c, 2026-09-27); a later surface may want the same for a
 *  served word's rendered width (bottom-bar slot reservation). */
export type FaceFn = (family: string, key: string) => string | null;

/** The value's COMPACT SPELLING: the tenant's served `abbr`, else its served label (honest, never a
 *  truncation a served row did not choose), else — only for an id the vocabulary does not serve at
 *  all — the adjudicated three-letter truncation. The code-owned abbreviation map between tiers 1
 *  and 2 is deleted (badge-faces round): every role serves its letters. */
export function resolveAbbr(vocab: ClassificationVocabulary | null | undefined, value: string): string {
  const served = servedValue(vocab, value);
  if (served?.abbr) return served.abbr;
  if (served) return served.label;
  return value.slice(0, 3).toUpperCase();
}

/** Pure helper: returns the UI noun for the classification axis.
 *  VNB (schema present) → 'Marktprozess'; gMSB/wMSB (no schema, legacy K-vocabulary role) →
 *  'Kategorie'; every other/unresolved role → the raw placeholder — same floor as resolveLabel,
 *  same reason: 'Kategorie' is gMSB/wMSB's own word, not a generic no-schema default. */
export function resolveCategoryNoun(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Marktprozess';
  return ownsMsbKVocabulary(role) ? 'Kategorie' : '{category.noun}';
}

/** Pure helper: UI noun for the SECOND classification axis.
 *  VNB (schema present) → 'Anliegen'; gMSB/wMSB (no schema) → 'Grund-Code'; every other/unresolved
 *  role → the raw placeholder. */
export function resolveSecondaryAxisNoun(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Anliegen';
  return ownsMsbKVocabulary(role) ? 'Grund-Code' : '{anliegen.noun}';
}

/** Pure helper: the PLURAL of the second-axis noun, for the sites that count things
 *  ("Häufigste …", "Alle …", "Weitere …").
 *  VNB → 'Anliegen' (invariant plural); gMSB/wMSB → 'Grund-Codes' (takes -s); every other/
 *  unresolved role → the raw placeholder.
 *  It lives beside resolveSecondaryAxisNoun rather than being derived from it because German
 *  plurals are not a suffix rule — deriving one would be right here and wrong at the next noun. */
export function resolveSecondaryAxisNounPlural(schema: ClassificationSchema | null, role: KnownRole | string | null | undefined): string {
  if (schema) return 'Anliegen';
  return ownsMsbKVocabulary(role) ? 'Grund-Codes' : '{anliegen.nounPlural}';
}

/** Tint a marktprozess hex into {fill,border,text} for gap chips / draft inputs.
 *  Fill = base blended ~90% toward white (a light tint); border/text = the base hue. */
export function marktprozessTint(hex: string): { fill: string; border: string; text: string } {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * 0.9);
  const fill = `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  return { fill, border: hex, text: hex };
}
